import React, {
	createRef,
	memo,
	useCallback,
	useMemo,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	Header,
	Menu,
	Modal,
	Tab,
	Transition
} from 'semantic-ui-react';
import html2canvas from 'html2canvas';

import {
	getDocument,
	updateSubmissionModuleData,
	submit,
	checkIntelligence,
	deleteSubmission
} from '../../api/nora/submissions';
import { useAuthState } from '../../contexts/AuthContext';
import {
	useSubmissionState,
	useSubmissionAction
} from '../../contexts/SubmissionsContext';
import { useUIAction } from '../../contexts/UIContext';

import DynamicFormModule from './Modules/DynamicFormModule';
import CollectionModule from './Modules/CollectionModule';
import FileModule from './Modules/FileModule';
import InstructionsModule from './Modules/InstructionsModule';
import QuestionaireModule from './Modules/QuestionaireModule';
import SubmitModule from './Modules/SubmitModule';

import styles from './document-modal.module.css';
import { uploadAsset } from '../../api/nora/assets';
import { setDocumentModulePhoto } from '../../api/nora/documentmodule';
import Alert from '../Alert';
import EvaluationModal from './EvaluationModal.js';

const DocumentModal = ( {
	admin,
	canSubmit = false,
	closeModal,
	onSubmit,
	open
} ) => {
	const { user: { permissions } } = useAuthState();
	const { document } = useSubmissionState();
	const { setDocument } = useSubmissionAction();
	const {
		setApiErrorAlert,
		setScreenLoader
	} = useUIAction();

	const modalRef = useRef();
	const moduleRefs = useRef( [] );
	const prevActiveIndex = useRef( 0 );

	const [ validateModule, setValidateModule ] = useState( false );
	const [ moduleErrors, setModuleErrors ] = useState( false );
	const [ activeIndex, setActiveIndex ] = useState( 0 );
	const [ moduleSavedAlert, setModuleSavedAlert ] = useState( false );
	const [ saving, setSaving ] = useState( false );
	const [ intelligenceConfirmModal, setIntelligenceConfirmModal ] = useState( '' );
	const [ intelligenceConfirmModalAction, setIntelligenceConfirmModalAction ] = useState( null );
	const [ deleting, setDeleting ] = useState( false );

	const handleSubmit = useCallback( async () => {
		try {
			if ( canSubmit ) {
				await submit( document );
				await onSubmit?.();
			}
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		closeModal();
	}, [
		canSubmit,
		closeModal,
		document,
		onSubmit,
		setApiErrorAlert
	] );

	const mapModules = useCallback( () => document?.modules?.map( ( module, i ) => {
		moduleRefs.current[ i + 1 ] = createRef();
		const moduleData = document.data.find( ( { moduleId } ) => moduleId === module.id );

		let renderItem;

		switch ( module.type.toLowerCase() ) {
			case 'dynamicform': {
				renderItem = (
					<Tab.Pane
						key={ module.id }
						as="div"
						className={ styles.activeTab }
					>
						<div
							ref={ modalRef }
							className={ styles.moduleModalRef }
						>
							<DynamicFormModule
								admin={ admin }
								instructions={ module.instructions }
								moduleData={ moduleData }
								ref={ moduleRefs.current[ i + 1 ] }
								sections={ module.sections }
								setModuleErrors={ setModuleErrors }
								ui={ module.fields }
								validate={ validateModule }
							/>
						</div>
					</Tab.Pane>
				);
				break;
			}

			case 'collection': {
				const {
					collectionLabel,
					itemLabel,
					labelComponent,
					row
				} = module.config;

				renderItem = (
					<Tab.Pane
						key={ module.id }
						as="div"
						className={ styles.activeTab }
					>
						<div
							ref={ modalRef }
							className={ styles.moduleModalRef }
						>
							<CollectionModule
								admin={ admin }
								collectionLabel={ collectionLabel }
								instructions={ module.instructions }
								itemLabel={ itemLabel }
								labelComponent={ labelComponent }
								moduleData={ moduleData }
								ref={ moduleRefs.current[ i + 1 ] }
								row={ row
									? row.split( ',' )
									: []
								}
								sections={ module.sections }
								ui={ module.fields }
							/>
						</div>
					</Tab.Pane>
				);
				break;
			}

			case 'file': {
				renderItem = (
					<Tab.Pane
						key={ module.id }
						as="div"
						className={ styles.activeTab }
					>
						<div
							ref={ modalRef }
							className={ styles.moduleModalRef }
						>
							<FileModule
								instructions={ module.instructions }
								moduleData={ moduleData }
								ref={ moduleRefs.current[ i + 1 ] }
								setModuleErrors={ setModuleErrors }
								fileItems={ module.fileItems }
								validate={ validateModule }
							/>
						</div>
					</Tab.Pane> );
				break;
			}

			case 'questionaire': {
				renderItem = (
					<Tab.Pane
						key={ module.id }
						as="div"
						className={ styles.activeTab }
					>
						<div
							ref={ modalRef }
							className={ styles.moduleModalRef }
						>
							<QuestionaireModule
								admin={ admin }
								instructions={ module.instructions }
								moduleData={ moduleData }
								ref={ moduleRefs.current[ i + 1 ] }
								sections={ module.sections }
								setModuleErrors={ setModuleErrors }
								ui={ module.fields }
								validate={ validateModule }
							/>
						</div>
					</Tab.Pane>
				);
				break;
			}

			default: {
				renderItem = <Tab.Pane
					key={ module.id }
					as="div"
				>
					<h1>{ module.title }</h1>
				</Tab.Pane>;
				break;
			}
		}

		return {
			key: module.id,
			menuItem: (
				<Menu.Item
					className={ styles.menuItem }
					color="pink"
					key={ module.id }
					style={ { flex: '1 0 150px' } }
				>
					<h4>{ module.title }</h4>
					<small>{ module.description }</small>
				</Menu.Item>
			),
			render: () => renderItem
		};
	} ), [
		admin,
		document,
		validateModule
	] );

	const modules = useMemo( () => mapModules() || [], [ mapModules ] );

	const panes = useMemo( () => [
		{
			key: 'instructionsModule',
			menuItem: (
				<Menu.Item
					className={ styles.menuItem }
					color="pink"
					key="instructionsModule"
					style={ { flex: '1 0 150px' } }
				>
					<h4>{ document?.type.id === 100 ? 'Agency Memo' : 'Instructions' }</h4>
					<small>Please read.</small>
				</Menu.Item>
			),
			// eslint-disable-next-line react/display-name
			render: () => (
				<Tab.Pane
					as="div"
					className={ styles.activeTab }
				>
					<InstructionsModule document={ document }/>
				</Tab.Pane>
			)
		},
		...modules,
		...canSubmit && document?.type.id !== 100
			? [
				{
					key: 'submitModule',
					menuItem: (
						<Menu.Item
							className={ styles.menuItem }
							color="pink"
							key="submitModule"
							style={ { flex: '1 0 150px' } }
						>
							<h4>Submit</h4>
							<small>Submit the document.</small>
						</Menu.Item>
					),
					// eslint-disable-next-line react/display-name
					render: () => (
						<Tab.Pane
							as="div"
							className={ styles.activeTab }
						>
							<SubmitModule
								moduleErrors={ moduleErrors }
								handleSubmit={ handleSubmit }
							/>
						</Tab.Pane>
					)
				}
			]
			: []
	], [
		canSubmit,
		document,
		handleSubmit,
		moduleErrors,
		modules
	] );

	const handleSave = useCallback( async data => {
		setModuleSavedAlert( true );
		setSaving( true );
		await updateSubmissionModuleData(
			document.id,
			data.moduleId,
			data
		);
		const updatedDoc = await getDocument( document.id );

		setTimeout( () => setModuleSavedAlert( false ), 500 );
		setDocument( updatedDoc );
	}, [ document?.id, setDocument ] );

	const handleTabChange = useCallback( async ( e, { activeIndex } ) => {
		if ( moduleErrors ) {
			setValidateModule( true );
		} else {
			setValidateModule( false );

			const i = prevActiveIndex.current;

			// if it isn't the last or first tab
			if ( !( canSubmit && i === panes.length - 1 ) && i !== 0 ) {
				const updatedData = moduleRefs.current[ i ].current.updateModuleData();

				if ( updatedData ) {
					// if module has a trigger
					if ( updatedData.config.intelligenceTrigger ) { // this will change when the trigger is moved to the data object
						try {
							const { valid, response } = await checkIntelligence( document.id, updatedData.moduleId, updatedData );

							if ( !valid ) {
								setIntelligenceConfirmModal( response );
								setIntelligenceConfirmModalAction( () => async () => {
									await handleSave( updatedData );

									// change the tab and update the prevActiveIndex ref
									setActiveIndex( activeIndex );
									prevActiveIndex.current = activeIndex;
								} );

								return;
							}
						} catch ( error ) {
							await handleSave( updatedData );
						}
					} else {
						await handleSave( updatedData );
					}
				}
			}

			// change the tab and update the prevActiveIndex ref
			setActiveIndex( activeIndex );
			prevActiveIndex.current = activeIndex;
		}
	}, [
		canSubmit,
		document?.id,
		handleSave,
		moduleErrors,
		panes.length
	] );

	const handleClose = useCallback( async () => {
		setActiveIndex( 0 );
		prevActiveIndex.current = 0;
		setModuleErrors( false );
		setValidateModule( false );
		await closeModal();
		setDocument( null );
	}, [
		closeModal,
		setDocument
	] );

	const handleDeleteSubmission = useCallback( async () => {
		setDeleting( true );

		try {
			await deleteSubmission( document?.id );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setDeleting( false );
		await handleClose();
	}, [
		document?.id,
		handleClose,
		setApiErrorAlert
	] );

	const handleSaveAndClose = useCallback( async () => {
		if ( moduleErrors ) {
			setValidateModule( true );
		} else {
			setValidateModule( false );

			// if it isn't the last or first tab
			if ( !( canSubmit && activeIndex === panes.length - 1 ) && activeIndex !== 0 ) {
				const updatedData = moduleRefs.current[ activeIndex ].current.updateModuleData();

				if ( updatedData ) {
					// if module has a trigger
					if ( updatedData.config.intelligenceTrigger ) { // this will change when the trigger is moved to the data object
						try {
							const { valid, response } = await checkIntelligence( document.id, updatedData.moduleId, updatedData );

							if ( !valid ) {
								setIntelligenceConfirmModal( response );
								setIntelligenceConfirmModalAction( () => async () => {
									await handleSave( updatedData );
									await handleClose();
								} );

								return;
							}
						} catch ( error ) {
							await handleSave( updatedData );
						}
					} else {
						await handleSave( updatedData );
					}
				}
			}

			handleClose();
		}
	}, [
		activeIndex,
		canSubmit,
		document?.id,
		handleClose,
		handleSave,
		moduleErrors,
		panes.length
	] );

	const handleSnapshot = useCallback( async () => {
		setScreenLoader( 'Taking Snapshot...' );

		try {
			const canvas = await html2canvas( modalRef.current, { scrollY: modalRef.current.scrollTop - modalRef.current.scrollHeight } );

			const moduleId = document?.modules?.[ activeIndex - 1 ].documentModuleId;

			canvas.toBlob( async blob => {
				const file = new File(
					[ blob ],
					`module_snapshot_${ moduleId }.jpg`
				);

				const data = new FormData();

				data.append( file.name, file );

				const { uid } = await uploadAsset( data );

				await setDocumentModulePhoto( moduleId, uid );
			}, 'image/jpeg' );
		} catch ( err ) {
			setApiErrorAlert( err );
		}

		setScreenLoader( '' );
	}, [
		activeIndex,
		document?.modules,
		setApiErrorAlert,
		setScreenLoader
	] );

	const closeIntelligenceConfirmModal = useCallback( () => {
		setIntelligenceConfirmModal( '' );
		setIntelligenceConfirmModalAction( null );
	}, [] );

	return (
		<>
			<Modal
				className={ styles.documentModal }
				closeOnDimmerClick={ false }
				open={ open }
			>
				<Tab
					activeIndex={ activeIndex }
					menu={ {
						attached: 'top',
						inverted: true,
						size: 'tiny',
						style: {
							margin: 0,
							overflowX: 'scroll',
							scrollbarColor: 'rgba(0, 0, 0, 0.25) transparent',
							scrollbarWidth: 'thin'
						}
					} }
					onTabChange={ handleTabChange }
					panes={ panes }
				/>
				<Modal.Actions style={ { display: 'flex' }}>
					{ document?.type.id === 99 && ( document?.status.id === 28 || document?.status.id === 29 )
						? <Button
							color="red"
							onClick={ handleDeleteSubmission }
							loading={ deleting }
						>
							Delete
						</Button>
						: null
					}
					{ activeIndex > 0 && permissions.snapshotDocumentModules.isAvailable
						? <Button
							disabled={ !permissions.snapshotDocumentModules.canExecute || document?.modules?.[ activeIndex - 1 ].documentModuleId === 0 }
							onClick={ handleSnapshot }
							icon="camera"
						/>
						: null
					}
					<Button
						onClick={ handleClose }
						style={{ marginLeft: 'auto' }}
					>
						Cancel
					</Button>
					<Button
						color="pink"
						onClick={ handleSaveAndClose }
					>
						Save and Close
					</Button>
				</Modal.Actions>
				<Transition
					duration={ { hide: 1000 } }
					visible={ moduleSavedAlert }
				>
					<Header
						as="h2"
						color="pink"
						style={ {
							position: 'absolute',
							bottom: '6.625rem',
							right: '3rem',
							zIndex: 1000,
							borderBottom: 'none',
							boxShadow: '1px 2px 4px 0 rgba(34, 36, 38, 0.15)'
						} }
					>
						{ saving
							? <span>
							Saving...
							</span>
							: 'Saved!'
						}
					</Header>
				</Transition>
			</Modal>
			{ open
				&& document
				&& document.eval?.evalID
				? <EvaluationModal open />
				: null
			}
			<Alert
				action={ intelligenceConfirmModalAction }
				name="intelligence-confirm-modal"
				message={ intelligenceConfirmModal }
				open={ Boolean( intelligenceConfirmModal ) }
				closeModal={ closeIntelligenceConfirmModal }
				type="warning"
			/>
		</>
	);
};

DocumentModal.propTypes = {
	admin: PropTypes.bool,
	canSubmit: PropTypes.bool,
	closeModal: PropTypes.func.isRequired,
	onSubmit: PropTypes.func,
	open: PropTypes.bool
};

export default memo( DocumentModal );
